import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { apiClient } from '../api/client';
import { formatAppError } from '../utils/errorMessage';
import GoogleAuthButton from './GoogleAuthButton';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getErrorCode = (err: any): string | null => {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
      return detail.code;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create form data for OAuth2PasswordRequestForm
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await apiClient.post('/api/v1/users/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      localStorage.setItem('token', response.data.access_token);
      // Dispatch custom event to notify navigation of auth change
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      navigate('/');
    } catch (err: any) {
      const status = err?.response?.status;
      const code = getErrorCode(err);
      const detailMessage = err?.response?.data?.detail;
      const shouldRedirectToVerify =
        status === 403 && (
          code === 'EMAIL_NOT_VERIFIED' ||
          (typeof detailMessage === 'string' && detailMessage.toLowerCase().includes('verify your email')) ||
          (typeof detailMessage === 'object' && typeof detailMessage?.message === 'string' && detailMessage.message.toLowerCase().includes('verify your email'))
        );

      if (shouldRedirectToVerify) {
        const normalizedEmail = email.trim();
        const query = normalizedEmail ? `?email=${encodeURIComponent(normalizedEmail)}` : '';
        navigate(`/verify-email-pending${query}`, { state: { email: normalizedEmail } });
        return;
      }

      const formatted = formatAppError(
        err,
        'login',
        'Unable to sign in right now. Please try again.',
        {
          401: 'Invalid email or password.',
          403: 'Please verify your email before logging in.',
          422: 'Please check your input and try again.',
        }
      );
      setError(formatted.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mb-6">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-slate-100 mb-2">
            Welcome Back, Builder
          </h2>
          <p className="text-slate-300">
            Sign in to test calls, manage API keys, and monitor usage.
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-slate-900/85 backdrop-blur-sm shadow-xl rounded-2xl border border-slate-700 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-300 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-500"
                placeholder="Enter your email"
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-semibold text-slate-300">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-500"
                placeholder="Enter your password"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm text-red-300">{error}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Need help? Contact us at{' '}
                        <a
                          href="mailto:support@emotionwise.ai"
                          className="text-indigo-300 hover:text-indigo-200 underline font-medium"
                        >
                          support@emotionwise.ai
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-900 px-2 text-slate-500">Or</span>
              </div>
            </div>

            <GoogleAuthButton
              mode="signin"
              onSuccess={() => navigate('/')}
              onError={(message) => setError(message)}
            />

            {/* Register Link */}
            <div className="text-center">
              <p className="text-sm text-slate-300">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="font-semibold text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center space-y-2">
          <Link
            to="/"
            className="text-sm text-slate-400 hover:text-slate-300 transition-colors duration-200"
          >
            ← Back to home
          </Link>
          <p className="text-xs text-slate-500">
            Having trouble signing in? Email us at{' '}
            <a 
              href="mailto:support@emotionwise.ai" 
              className="text-slate-300 hover:text-slate-200 underline"
            >
              support@emotionwise.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
} 
